"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, ChevronLeft, ChevronRight, Heart, X } from "lucide-react";
import type { Gift, GiftPhoto } from "@/types";
import { cn } from "@/lib/utils";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import {
  getExperienceData,
  type GalleryExperienceData,
  type GalleryPhotoData,
} from "@/data/experienceDataSchema";

export type GalleryVariant = "default" | "spotify";

export interface GalleryPalette {
  accent: string;
  accentRgb: string;
}

interface GallerySectionProps {
  gift: Partial<Gift>;
  variant: GalleryVariant;
  palette?: GalleryPalette;
  className?: string;
}

interface NormalizedPhoto {
  id: string;
  url: string;
  caption?: string;
}

const MAX_PHOTOS = 12;

const DEFAULT_SPOTIFY_PALETTE: GalleryPalette = {
  accent: "#1DB954",
  accentRgb: "29,185,84",
};

// Algumas fotos podem chegar com diferentes formatos dependendo de fluxos
// antigos do projeto (url, src, imageUrl). Mantemos um helper único, leve e
// tolerante para extrair a URL sem quebrar o tipo principal GiftPhoto.
function extractPhotoUrl(photo: unknown): string | null {
  if (!photo || typeof photo !== "object") return null;
  const candidate = photo as Record<string, unknown>;
  const url = candidate.url ?? candidate.src ?? candidate.imageUrl;
  if (typeof url !== "string") return null;
  const trimmed = url.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function extractPhotoCaption(photo: unknown): string | undefined {
  if (!photo || typeof photo !== "object") return undefined;
  const candidate = photo as Record<string, unknown>;
  const caption = candidate.caption ?? candidate.description ?? candidate.title;
  if (typeof caption !== "string") return undefined;
  const trimmed = caption.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizePhotos(photos: GiftPhoto[] | undefined): NormalizedPhoto[] {
  if (!Array.isArray(photos) || photos.length === 0) return [];
  const result: NormalizedPhoto[] = [];
  for (const raw of photos) {
    const url = extractPhotoUrl(raw);
    if (!url) continue;
    const candidate = raw as unknown as Record<string, unknown>;
    const id =
      typeof candidate.id === "string" && candidate.id.length > 0
        ? candidate.id
        : `gallery-photo-${result.length}`;
    result.push({ id, url, caption: extractPhotoCaption(raw) });
    if (result.length >= MAX_PHOTOS) break;
  }
  return result;
}

// Fotos extras personalizadas pelo usuário (experienceData.gallery.photos).
// Adicionamos depois das fotos do passo "Fotos" para enriquecer a galeria
// sem quebrar quem só carregou fotos pelo passo principal.
function normalizeCustomPhotos(
  photos: GalleryPhotoData[] | undefined
): NormalizedPhoto[] {
  if (!Array.isArray(photos) || photos.length === 0) return [];
  const result: NormalizedPhoto[] = [];
  for (const photo of photos) {
    const url = photo?.url?.trim();
    if (!url) continue;
    result.push({
      id: `gallery-custom-${result.length}`,
      url,
      caption: photo.caption?.trim() ? photo.caption.trim() : undefined,
    });
  }
  return result;
}

// Pequenas rotações alternadas dão o ar de polaroid sem quebrar o alinhamento
// no desktop. No mobile zeramos a rotação para garantir leitura limpa.
const POLAROID_ROTATIONS = ["-2.5deg", "1.8deg", "-1.2deg", "2.4deg", "-2deg"];
function getRotationFor(index: number): string {
  return POLAROID_ROTATIONS[index % POLAROID_ROTATIONS.length];
}

export function GallerySection({
  gift,
  variant,
  palette,
  className,
}: GallerySectionProps) {
  const customData = useMemo(
    () => getExperienceData<GalleryExperienceData>(gift, "gallery"),
    [gift]
  );
  const photos = useMemo(() => {
    const base = normalizePhotos(gift.photos);
    const extras = normalizeCustomPhotos(customData?.photos);
    const merged = [...base, ...extras];
    return merged.slice(0, MAX_PHOTOS);
  }, [gift.photos, customData]);
  const customTitle = customData?.title?.trim();
  const customSubtitle = customData?.subtitle?.trim();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const closeLightbox = useCallback(() => setActiveIndex(null), []);
  const openLightbox = useCallback((index: number) => setActiveIndex(index), []);
  const showPrev = useCallback(() => {
    setActiveIndex((current) =>
      current === null ? current : (current - 1 + photos.length) % photos.length
    );
  }, [photos.length]);
  const showNext = useCallback(() => {
    setActiveIndex((current) =>
      current === null ? current : (current + 1) % photos.length
    );
  }, [photos.length]);

  useBodyScrollLock(activeIndex !== null);

  useEffect(() => {
    if (activeIndex === null) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeLightbox();
      else if (event.key === "ArrowLeft") showPrev();
      else if (event.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, [activeIndex, closeLightbox, showPrev, showNext]);

  const isSpotify = variant === "spotify";
  const effectivePalette = palette ?? DEFAULT_SPOTIFY_PALETTE;

  return (
    <>
      {isSpotify ? (
        <SpotifyGallery
          photos={photos}
          palette={effectivePalette}
          onOpen={openLightbox}
          className={className}
          giftTitle={gift.title}
          title={customTitle}
          subtitle={customSubtitle}
        />
      ) : (
        <DefaultGallery
          photos={photos}
          onOpen={openLightbox}
          className={className}
          giftTitle={gift.title}
          title={customTitle}
          subtitle={customSubtitle}
        />
      )}

      <Lightbox
        photos={photos}
        activeIndex={activeIndex}
        onClose={closeLightbox}
        onPrev={showPrev}
        onNext={showNext}
        variant={variant}
        palette={effectivePalette}
      />
    </>
  );
}

// ─── Variante default — clara, romântica, premium ───────────────────────────

interface DefaultGalleryProps {
  photos: NormalizedPhoto[];
  onOpen: (index: number) => void;
  className?: string;
  giftTitle?: string;
  title?: string;
  subtitle?: string;
}

function DefaultGallery({
  photos,
  onOpen,
  className,
  giftTitle,
  title,
  subtitle,
}: DefaultGalleryProps) {
  const hasFeatured = photos.length >= 3;
  const featured = hasFeatured ? photos[0] : null;
  const restPhotos = hasFeatured ? photos.slice(1) : photos;

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        "gallery-section-default relative overflow-hidden rounded-3xl border shadow-sm",
        className
      )}
      style={{
        background:
          "linear-gradient(135deg, #FFE4E6 0%, #FFF7ED 55%, #FFE4F1 100%)",
        borderColor: "rgba(225,29,72,0.18)",
      }}
    >
      <DefaultDecorations />

      <div className="relative z-10 flex flex-col gap-6 p-6 sm:p-8">
        <header className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337] shadow-sm backdrop-blur">
            <Camera className="h-3 w-3 text-[#E11D48]" />
            Nossa Galeria
          </span>
          <h2
            className="text-2xl sm:text-3xl font-semibold leading-tight text-[#1F2937]"
            style={{ fontFamily: "var(--font-serif, serif)" }}
          >
            {title || "Fotos que contam a nossa história"}
          </h2>
          <p className="max-w-sm text-sm text-[#1F2937]/70 leading-relaxed">
            {subtitle || "Os instantes favoritos que escolhemos guardar para sempre."}
          </p>
        </header>

        {photos.length === 0 ? (
          <DefaultEmptyState />
        ) : (
          <div className="flex flex-col gap-5">
            {featured ? (
              <FeaturedPolaroid
                photo={featured}
                onClick={() => onOpen(0)}
                giftTitle={giftTitle}
              />
            ) : null}

            <div
              className={cn(
                "grid gap-4 sm:gap-5",
                restPhotos.length === 1 && "grid-cols-1",
                restPhotos.length === 2 && "grid-cols-1 sm:grid-cols-2",
                restPhotos.length >= 3 &&
                  "grid-cols-2 sm:grid-cols-2 md:grid-cols-3"
              )}
            >
              {restPhotos.map((photo, index) => {
                const realIndex = hasFeatured ? index + 1 : index;
                return (
                  <PolaroidCard
                    key={photo.id}
                    photo={photo}
                    index={index}
                    onClick={() => onOpen(realIndex)}
                    giftTitle={giftTitle}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.section>
  );
}

interface PolaroidCardProps {
  photo: NormalizedPhoto;
  index: number;
  onClick: () => void;
  giftTitle?: string;
}

function FeaturedPolaroid({
  photo,
  onClick,
  giftTitle,
}: Omit<PolaroidCardProps, "index">) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -4, rotate: 0 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
      className="group relative mx-auto w-full max-w-md cursor-pointer rounded-[20px] bg-white p-3 sm:p-4 text-left shadow-[0_18px_40px_-18px_rgba(136,19,55,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E11D48]"
      style={{ transform: "rotate(-1.2deg)" }}
      aria-label={photo.caption ?? `Foto principal — ${giftTitle ?? "galeria"}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-[12px] bg-[#FFE4E6]">
        <PhotoImage
          photo={photo}
          giftTitle={giftTitle}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 px-1">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#881337]/80">
          Nosso destaque
        </span>
        <Heart className="h-4 w-4 fill-[#E11D48] text-[#E11D48]" />
      </div>
      {photo.caption ? (
        <p className="mt-1 px-1 text-sm italic text-[#1F2937]/80 leading-snug">
          {photo.caption}
        </p>
      ) : null}
    </motion.button>
  );
}

function PolaroidCard({ photo, index, onClick, giftTitle }: PolaroidCardProps) {
  const rotation = getRotationFor(index);
  const style: CSSProperties = {
    // Aplicamos a rotação como variável para que possa ser zerada no mobile
    // via media-query inline; em desktop os pequenos ângulos compõem o look.
    transform: `rotate(var(--polaroid-rotation, ${rotation}))`,
  };

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -4, rotate: 0 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
      className="group polaroid-card relative cursor-pointer rounded-2xl bg-white p-2.5 text-left shadow-[0_12px_28px_-14px_rgba(136,19,55,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E11D48]"
      style={style}
      aria-label={
        photo.caption ?? `Foto ${index + 2} — ${giftTitle ?? "galeria"}`
      }
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-[#FFE4E6]">
        <PhotoImage
          photo={photo}
          giftTitle={giftTitle}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      {photo.caption ? (
        <p className="mt-2 px-1 text-[11px] text-[#1F2937]/75 leading-snug line-clamp-2">
          {photo.caption}
        </p>
      ) : (
        <div className="mt-2 flex items-center gap-1.5 px-1">
          <span className="h-1 w-1 rounded-full bg-[#E11D48]/60" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#881337]/50">
            Memória
          </span>
        </div>
      )}
      <style jsx>{`
        @media (max-width: 640px) {
          .polaroid-card {
            --polaroid-rotation: 0deg;
          }
        }
      `}</style>
    </motion.button>
  );
}

function DefaultEmptyState() {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-[#E11D48]/30 bg-white/60 px-6 py-10 text-center backdrop-blur">
      <Heart className="h-6 w-6 text-[#E11D48]" />
      <p className="text-sm text-[#1F2937]/70 italic leading-relaxed">
        As fotos dessa história ainda serão adicionadas aqui.
      </p>
    </div>
  );
}

function DefaultDecorations() {
  return (
    <>
      <Heart
        className="absolute -top-4 -right-4 h-24 w-24 text-[#E11D48]/15"
        fill="currentColor"
      />
      <Heart
        className="absolute -bottom-3 -left-3 h-16 w-16 text-[#F59E0B]/20"
        fill="currentColor"
      />
    </>
  );
}

// ─── Variante Spotify — escura, imersiva ────────────────────────────────────

interface SpotifyGalleryProps {
  photos: NormalizedPhoto[];
  palette: GalleryPalette;
  onOpen: (index: number) => void;
  className?: string;
  giftTitle?: string;
  title?: string;
  subtitle?: string;
}

function SpotifyGallery({
  photos,
  palette,
  onOpen,
  className,
  giftTitle,
  title,
  subtitle,
}: SpotifyGalleryProps) {
  const featured = photos.length >= 3 ? photos[0] : null;
  const restPhotos = featured ? photos.slice(1) : photos;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "gallery-section-spotify relative overflow-hidden rounded-2xl",
        className
      )}
      style={{
        background: `radial-gradient(120% 100% at 0% 0%, rgba(${palette.accentRgb},0.22) 0%, rgba(${palette.accentRgb},0.06) 45%, rgba(15,15,18,0.95) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.25)`,
      }}
    >
      <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-5">
        <header className="flex items-center gap-2">
          <span style={{ color: palette.accent }}>
            <Camera className="h-3.5 w-3.5" />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: palette.accent }}
          >
            Nossa Galeria
          </span>
        </header>

        <div className="flex flex-col gap-1">
          <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
            {title || "Memórias favoritas"}
          </h3>
          <p className="text-[11px] text-white/55 tracking-wide">
            {subtitle ||
              "Cliques que ficaram marcados na trilha sonora desse amor."}
          </p>
        </div>

        {photos.length === 0 ? (
          <SpotifyEmptyState palette={palette} />
        ) : (
          <div className="flex flex-col gap-3">
            {featured ? (
              <SpotifyFeaturedCard
                photo={featured}
                palette={palette}
                onClick={() => onOpen(0)}
                giftTitle={giftTitle}
              />
            ) : null}

            <div
              className={cn(
                "grid gap-2",
                restPhotos.length === 1 && "grid-cols-1",
                restPhotos.length === 2 && "grid-cols-2",
                restPhotos.length >= 3 && "grid-cols-2 sm:grid-cols-3"
              )}
            >
              {restPhotos.map((photo, index) => {
                const realIndex = featured ? index + 1 : index;
                return (
                  <SpotifyPhotoCard
                    key={photo.id}
                    photo={photo}
                    palette={palette}
                    onClick={() => onOpen(realIndex)}
                    giftTitle={giftTitle}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.section>
  );
}

interface SpotifyCardProps {
  photo: NormalizedPhoto;
  palette: GalleryPalette;
  onClick: () => void;
  giftTitle?: string;
}

function SpotifyFeaturedCard({
  photo,
  palette,
  onClick,
  giftTitle,
}: SpotifyCardProps) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: "spring", stiffness: 240, damping: 20 }}
      className="group relative w-full cursor-pointer overflow-hidden rounded-xl text-left focus:outline-none focus-visible:ring-2"
      style={{
        boxShadow: `0 18px 40px -18px rgba(${palette.accentRgb},0.45)`,
        // @ts-expect-error CSS custom property for focus ring color
        "--tw-ring-color": palette.accent,
      }}
      aria-label={photo.caption ?? `Foto principal — ${giftTitle ?? "galeria"}`}
    >
      <div className="relative aspect-[16/10] w-full">
        <PhotoImage
          photo={photo}
          giftTitle={giftTitle}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-3">
          <span
            className="text-[9px] font-bold uppercase tracking-[0.22em]"
            style={{ color: palette.accent }}
          >
            Em destaque
          </span>
          <p className="text-sm font-semibold text-white line-clamp-2">
            {photo.caption ?? "Um instante para ouvir no repeat."}
          </p>
        </div>
      </div>
    </motion.button>
  );
}

function SpotifyPhotoCard({
  photo,
  palette,
  onClick,
  giftTitle,
}: SpotifyCardProps) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
      className="group relative cursor-pointer overflow-hidden rounded-lg text-left focus:outline-none focus-visible:ring-2"
      style={{
        background: `rgba(${palette.accentRgb},0.06)`,
        border: `1px solid rgba(${palette.accentRgb},0.18)`,
        // @ts-expect-error CSS custom property for focus ring color
        "--tw-ring-color": palette.accent,
      }}
      aria-label={photo.caption ?? `Foto — ${giftTitle ?? "galeria"}`}
    >
      <div className="relative aspect-square w-full">
        <PhotoImage
          photo={photo}
          giftTitle={giftTitle}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {photo.caption ? (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
            <p className="text-[10px] text-white/85 line-clamp-2 leading-snug">
              {photo.caption}
            </p>
          </div>
        ) : null}
      </div>
    </motion.button>
  );
}

function SpotifyEmptyState({ palette }: { palette: GalleryPalette }) {
  return (
    <div
      className="flex flex-col items-center gap-2 rounded-xl px-4 py-6 text-center"
      style={{
        background: `rgba(${palette.accentRgb},0.06)`,
        border: `1px dashed rgba(${palette.accentRgb},0.25)`,
      }}
    >
      <Camera className="h-5 w-5" style={{ color: palette.accent }} />
      <p className="text-[12px] text-white/70 leading-relaxed">
        As fotos dessa história ainda serão adicionadas aqui.
      </p>
    </div>
  );
}

// ─── Lightbox ───────────────────────────────────────────────────────────────

interface LightboxProps {
  photos: NormalizedPhoto[];
  activeIndex: number | null;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  variant: GalleryVariant;
  palette: GalleryPalette;
}

function Lightbox({
  photos,
  activeIndex,
  onClose,
  onPrev,
  onNext,
  variant,
  palette,
}: LightboxProps) {
  const photo = activeIndex !== null ? photos[activeIndex] : null;
  const accentColor = variant === "spotify" ? palette.accent : "#E11D48";
  const hasMultiple = photos.length > 1;

  return (
    <AnimatePresence>
      {photo ? (
        <motion.div
          key="gallery-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/85 p-4 backdrop-blur"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="Visualização ampliada da foto"
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onClose();
            }}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Fechar visualização"
          >
            <X className="h-5 w-5" />
          </button>

          {hasMultiple ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onPrev();
              }}
              className="absolute left-2 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:left-6"
              aria-label="Foto anterior"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          ) : null}

          {hasMultiple ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onNext();
              }}
              className="absolute right-2 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-6"
              aria-label="Próxima foto"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          ) : null}

          <motion.div
            key={photo.id}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            // Swipe gesture pra mobile: arrastar a foto pros lados troca.
            // dragConstraints={{ left: 0, right: 0 }} faz o framer-motion
            // permitir o gesto MAS reverter visualmente — só usamos pra
            // detectar a direção em onDragEnd.
            drag={hasMultiple ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.4}
            onDragEnd={(_, info) => {
              if (!hasMultiple) return;
              const SWIPE_THRESHOLD = 60; // px
              const VELOCITY_THRESHOLD = 300;
              if (
                info.offset.x < -SWIPE_THRESHOLD ||
                info.velocity.x < -VELOCITY_THRESHOLD
              ) {
                onNext();
              } else if (
                info.offset.x > SWIPE_THRESHOLD ||
                info.velocity.x > VELOCITY_THRESHOLD
              ) {
                onPrev();
              }
            }}
            className="relative flex max-h-[90vh] w-full max-w-3xl flex-col items-center gap-3 touch-pan-y"
            style={{ cursor: hasMultiple ? "grab" : "default" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative w-full overflow-hidden rounded-2xl bg-black/40 shadow-2xl">
              <PhotoImage
                photo={photo}
                className="block max-h-[78vh] w-full object-contain select-none pointer-events-none"
              />
            </div>
            {photo.caption ? (
              <p className="max-w-2xl text-center text-sm text-white/85 italic">
                {photo.caption}
              </p>
            ) : null}
            {hasMultiple ? (
              <div className="flex flex-col items-center gap-1">
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.22em]"
                  style={{ color: accentColor }}
                >
                  {(activeIndex ?? 0) + 1} / {photos.length}
                </p>
                <p className="text-[10px] text-white/40 sm:hidden">
                  Arraste pra trocar
                </p>
              </div>
            ) : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

// ─── Imagem reutilizável ────────────────────────────────────────────────────

interface PhotoImageProps {
  photo: NormalizedPhoto;
  className?: string;
  giftTitle?: string;
}

function PhotoImage({ photo, className, giftTitle }: PhotoImageProps) {
  const alt =
    photo.caption ?? (giftTitle ? `Foto de ${giftTitle}` : "Foto da galeria");
  return (
    // Usamos <img> nativo para suportar URLs vindas de qualquer host de fotos
    // sem depender da configuração de remotePatterns do next/image.
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={photo.url}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={className}
      style={{ objectPosition: "center" }}
    />
  );
}
